import { AbstractBuildstep } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import filter from "lodash/filter";
import find from "lodash/find";
import * as THREE from "three";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PanelColor } from "../interfaces/PanelColor";
import type { SlideRailDefinition } from "../interfaces/RollerPanelProperties";
import type { RunnerInfos } from "../interfaces/RunnerInfos";
import type { RunnerType } from "../interfaces/RunnerType";
import { DefaultGroupName } from "../structure/Rect";
import { RectSizeHelper } from "../structure/RectSizeHelper";

// Standard width for runners (if needed, otherwise size comes from calculation)
// const RUNNER_WIDTH = 50;

// --- Dependency Data Interface ---
interface RunnerDependencyData {
  partId: string;
  groupName: string;
  buildstepName: string;
  partKind: "Runner";
  type: LTPartType.SlideRail;
  offset: number;
  position: THREE.Vector3;
  size: THREE.Vector2;
  side: Sides.BOTTOM;
  partSelection: RunnerType;
  color: PanelColor;
}

// --- Refactored Build Step ---
export class RunnerBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  SlideRailDefinition, // Output type
  RunnerDependencyData // Dependency data type
> {
  constructor() {
    super("RunnerBuildStep");
  }

  // --- Helpers to reduce complexity ---
  private getBaseRect(
    blueprint: FlightCaseBlueprint,
  ): FlightCaseBlueprint["rects"][number] | undefined {
    return find(blueprint.rects, {
      side: Sides.BOTTOM,
      group: DefaultGroupName,
    });
  }

  private computeXPos(
    baseRect: FlightCaseBlueprint["rects"][number],
    count: number,
    index: number,
    distanceFromEdge: number,
    runnerWidth: number,
  ): number {
    const distFromCenter =
      baseRect.outerSize.x / 2 - runnerWidth / 2 - distanceFromEdge;
    if (count === 1) return baseRect.center.x; // single centered
    return (
      baseRect.center.x +
      (2 * distFromCenter * (index - (count - 1) / 2)) / (count - 1)
    );
  }

  private computeRunnerSize(
    baseRect: FlightCaseBlueprint["rects"][number],
    exteriorOptions: FlightCaseSettings["exteriorOptions"],
    runnerWidth: number,
    info: RunnerInfos,
  ): THREE.Vector2 {
    const custom = info.length;
    if (typeof custom === "number" && Number.isFinite(custom) && custom > 0) {
      return new THREE.Vector2(custom, runnerWidth);
    }
    // Mirror legacy RunnerBuilder: decide per end-edge which profile the runner
    // terminates at. FROMCUT edges meet a closing profile (Schließprofil),
    // all other edges meet a Kantenschutz (angleSection). fullLength runners
    // extend over the terminating profile; otherwise they are inset.
    const { angleSections, profiles } = exteriorOptions;
    const fullLength = info.fullLength === true;
    const sizeHelper = new RectSizeHelper(baseRect, false);
    for (const edge of [EdgeIndex.BOTTOM, EdgeIndex.TOP]) {
      const isFromCut = baseRect.edges?.[edge]?.type === EdgeType.FROMCUT;
      if (isFromCut) {
        sizeHelper.expand(
          edge,
          fullLength
            ? profiles.strength
            : -(profiles.height + profiles.extra / 2),
        );
      } else {
        const strength = angleSections?.strength ?? 0;
        const depth = angleSections?.depth ?? 0;
        sizeHelper.expand(edge, fullLength ? strength : -depth);
      }
    }
    return new THREE.Vector2(sizeHelper.size.y, runnerWidth);
  }

  private createRunnerDependency(args: {
    buildstepName: string;
    groupName: string;
    partId: string;
    xPos: number;
    yPos: number;
    zPos: number;
    size: THREE.Vector2;
    centerX: number;
    partSelection: RunnerType;
    color: PanelColor;
  }): RunnerDependencyData {
    const {
      buildstepName,
      groupName,
      partId,
      xPos,
      yPos,
      zPos,
      size,
      centerX,
      partSelection,
      color,
    } = args;
    const position = new THREE.Vector3(xPos, yPos, zPos);
    return {
      partId,
      partKind: "Runner",
      type: LTPartType.SlideRail,
      groupName,
      buildstepName,
      offset: xPos - centerX,
      position,
      size,
      side: Sides.BOTTOM,
      partSelection,
      color,
    };
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, RunnerDependencyData> {
    const dependencies = new Map<string, RunnerDependencyData>();
    let runnerCounter = 0;

    const runners = filter(settings.variableOptions, {
      variableOptionType: VariableOptionType.RUNNERS,
    });

    const baseRect = this.getBaseRect(blueprint);
    if (!baseRect) {
      console.warn(
        "RunnerBuildStep: Could not find base bottom rect. Skipping runner generation.",
      );
      return dependencies;
    }

    for (const runner of runners) {
      if (runner.variableOptionType !== VariableOptionType.RUNNERS) continue;
      const info = runner as RunnerInfos;
      const {
        count,
        partSelection: { distanceFromEdge, width: selWidth },
      } = info;
      const exterior = settings.exteriorOptions;
      const panelStrength = exterior.strength ?? 0;
      const angleSectionStrength = exterior.angleSections?.strength ?? 0;
      for (let n = 0; n < count; n++) {
        const RUNNER_WIDTH = selWidth ?? 50;
        const xPos = this.computeXPos(
          baseRect,
          count,
          n,
          distanceFromEdge,
          RUNNER_WIDTH,
        );
        const size = this.computeRunnerSize(
          baseRect,
          exterior,
          RUNNER_WIDTH,
          info,
        );

        const runnerStrength = info.partSelection.strength ?? 9;
        const angleYInset = info.fullLength === true ? angleSectionStrength : 0;
        const yPos =
          -blueprint.innerDimensions.y / 2 -
          panelStrength -
          angleYInset -
          runnerStrength / 2;
        const partId = `runner_${runnerCounter++}_${n}`;
        const dep = this.createRunnerDependency({
          buildstepName: this.buildstepName,
          groupName: DefaultGroupName,
          partId,
          xPos,
          yPos,
          zPos: baseRect.center.z,
          size: size.clone(),
          centerX: baseRect.center.x,
          partSelection: info.partSelection,
          color: settings.exteriorOptions.color,
        });
        dependencies.set(partId, dep);
      }
    }
    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: RunnerDependencyData,
  ): SlideRailDefinition {
    // Rotation seems constant for runners on the bottom
    const finalRotation = this.rotateOntoSide(Sides.BOTTOM, 90);

    return {
      id: partId,
      type: LTPartType.SlideRail,
      group: dependencies.groupName,
      buildstepName: dependencies.buildstepName,
      offset: dependencies.offset,
      position: dependencies.position.clone(),
      size: dependencies.size.clone(),
      partSelection: dependencies.partSelection,
      rotation: finalRotation,
      orientation: 90,
    };
  }

  // Old execute method removed
}
